import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { CssVarsProvider } from "@mui/joy/styles";
import customTheme from "./theme";
import ErrorBoundary from "./components/ErrorBoundary";
// i18n 需要同步加载，因为首屏组件会立即使用翻译
// 但已在 i18n.js 中优化了加载策略（initImmediate: false）
import "./i18n.js";

// Defer non-critical error handlers to avoid blocking initial render
// Use requestIdleCallback to delay setup until browser is idle
const setupErrorHandlers = () => {
  // 全局错误处理：捕获第三方追踪库（如 Amplitude）的错误，避免影响应用正常运行
  window.addEventListener("error", (event) => {
    // 检查错误是否来自 Amplitude 或其他追踪服务
    const isTrackingError = 
      event.filename?.includes("amplitude.com") || 
      event.filename?.includes("session-replay") ||
      event.message?.includes("Failed to fetch");
    
    if (isTrackingError) {
      console.warn("追踪服务错误（已忽略）:", event.message);
      event.preventDefault(); // 阻止错误继续传播
      return true;
    }
  });

  // 捕获 Promise 中未处理的追踪错误
  window.addEventListener("unhandledrejection", (event) => {
    const errorMessage = event.reason?.message || String(event.reason);
    const stack = event.reason?.stack || "";
    
    const isTrackingError = 
      stack.includes("amplitude.com") || 
      stack.includes("session-replay") ||
      errorMessage.includes("Failed to fetch");
    
    if (isTrackingError) {
      console.warn("追踪服务 Promise 错误（已忽略）:", errorMessage);
      event.preventDefault(); // 阻止错误继续传播
    }
  });
};

// Delay error handler setup until after initial render
if (typeof window !== "undefined") {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(setupErrorHandlers, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(setupErrorHandlers, 0);
  }
}

// Defer device detection to avoid blocking initial render
const setupDeviceDetection = () => {
  function isAppleDevice(): boolean {
    const ua = navigator.userAgent || "";
    const platform = navigator.platform || "";
    const maxTouchPoints = (navigator as any).maxTouchPoints || 0;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && maxTouchPoints > 1);
    const isMacOS = /Macintosh|Mac OS X/.test(ua);
    return isIOS || isMacOS;
  }

  if (typeof document !== "undefined" && isAppleDevice()) {
    document.body.classList.add("is-apple");
  }
};

// Delay device detection until after initial render
if (typeof window !== "undefined") {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(setupDeviceDetection);
  } else {
    setTimeout(setupDeviceDetection, 0);
  }
}

// Bundle 7.A Phase 3 — Sentry frontend init
// DSN read from env var; no-ops if unset (no console errors, no throws)
const sentryDsn = process.env.REACT_APP_SENTRY_DSN_FRONTEND;

/**
 * Redact bearer credentials from anything on its way to Sentry.
 *
 * Two carriers, and the URL strip in public/index.html only closes the first:
 *   - the page URL /presente/<token>, /convite/<token>
 *   - the claim API call itself, /api/gift/claim/<token>/ — the token is in the
 *     request path, so it lands in Sentry's xhr breadcrumbs regardless of what
 *     the address bar says. This is the only fix for that channel.
 */
const scrubBearer = (value: string): string =>
  value
    .replace(/(\/(?:presente|convite)\/)[A-Za-z0-9_-]+/g, '$1:token')
    .replace(/(\/api\/gift\/claim\/)[A-Za-z0-9_-]+/g, '$1:token');

if (sentryDsn) {
  import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: sentryDsn,
      environment: process.env.REACT_APP_ENV || 'production',
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,

      beforeBreadcrumb(breadcrumb) {
        // xhr/fetch breadcrumbs carry the request URL; navigation breadcrumbs
        // carry from/to paths.
        const data = breadcrumb.data as Record<string, unknown> | undefined;
        if (data) {
          for (const key of ['url', 'from', 'to']) {
            if (typeof data[key] === 'string') {
              data[key] = scrubBearer(data[key] as string);
            }
          }
        }
        if (typeof breadcrumb.message === 'string') {
          breadcrumb.message = scrubBearer(breadcrumb.message);
        }
        return breadcrumb;
      },

      beforeSend(event) {
        // httpContextIntegration stamps the full href onto every error event.
        if (event.request?.url) {
          event.request.url = scrubBearer(event.request.url);
        }
        const referer = event.request?.headers?.Referer;
        if (typeof referer === 'string') {
          event.request!.headers!.Referer = scrubBearer(referer);
        }
        if (event.transaction) {
          event.transaction = scrubBearer(event.transaction);
        }
        return event;
      },

      beforeSendTransaction(event) {
        // Pre-arms the trap: adding browserTracingIntegration would otherwise
        // start sending the unparameterised /presente/<token> as the txn name.
        if (event.transaction) {
          event.transaction = scrubBearer(event.transaction);
        }
        if (event.request?.url) {
          event.request.url = scrubBearer(event.request.url);
        }
        return event;
      },
    });
  }).catch(() => { /* Sentry load failure — non-critical */ });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
// Inject spin keyframe for Suspense loader
const _spinStyle = document.createElement("style");
_spinStyle.textContent = "@keyframes _i18nSpin { to { transform: rotate(360deg); } }";
document.head.appendChild(_spinStyle);

root.render(
  <React.StrictMode>
    <Suspense
      fallback={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#0a0a1a" }}>
          <div style={{ width: 40, height: 40, border: "3px solid #FFD900", borderTopColor: "transparent", borderRadius: "50%", animation: "_i18nSpin 0.8s linear infinite" }} />
        </div>
      }
    >
      <ErrorBoundary>
        <CssVarsProvider theme={customTheme}>
          <App />
        </CssVarsProvider>
      </ErrorBoundary>
    </Suspense>
  </React.StrictMode>
);
